import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Mail, 
  Phone, 
  Building,
  MapPin,
  Calendar,
  Star,
  TrendingUp,
  Users,
  UserPlus,
  Briefcase,
  User
} from 'lucide-react';

const Contacts = () => {
  const { contacts, investors, salesLeads, candidates, addContact, updateContact, deleteContact } = useCRM();
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'contacts_only'

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    type: 'general',
    location: '',
    notes: '',
    linkedinUrl: '',
    website: '',
    tags: '',
    lastContact: ''
  });

  const contactTypes = [
    { value: 'general', label: 'General Contact', icon: User, color: 'text-gray-600' },
    { value: 'mentor', label: 'Mentor/Advisor', icon: Star, color: 'text-yellow-600' },
    { value: 'partner', label: 'Business Partner', icon: Briefcase, color: 'text-blue-600' },
    { value: 'vendor', label: 'Vendor/Service', icon: Building, color: 'text-green-600' },
    { value: 'media', label: 'Media/Press', icon: Users, color: 'text-purple-600' },
    { value: 'customer', label: 'Customer', icon: Star, color: 'text-orange-600' },
    { value: 'other', label: 'Other', icon: User, color: 'text-gray-600' }
  ];

  // Consolidate all contacts from different sources
  const allContacts = [
    ...contacts.map(contact => ({
      ...contact,
      source: 'contacts',
      sourceLabel: 'General Contact',
      sourceIcon: User,
      sourceColor: 'bg-gray-100 text-gray-800'
    })),
    ...investors.map(investor => ({
      id: `investor-${investor.id}`,
      name: investor.name,
      email: investor.email,
      phone: investor.phone,
      company: investor.firm,
      position: 'Investor',
      type: 'investor',
      notes: investor.notes,
      linkedinUrl: investor.linkedinUrl,
      lastContact: investor.lastContact,
      source: 'investors',
      sourceLabel: 'Investor',
      sourceIcon: TrendingUp,
      sourceColor: 'bg-green-100 text-green-800',
      originalData: investor
    })),
    ...salesLeads.map(lead => ({
      id: `lead-${lead.id}`,
      name: lead.contactName,
      email: lead.email,
      phone: lead.phone,
      company: lead.companyName,
      position: 'Sales Contact',
      type: 'sales_lead',
      notes: lead.notes,
      website: lead.website,
      lastContact: lead.lastContact,
      source: 'sales_leads',
      sourceLabel: 'Sales Lead',
      sourceIcon: Users,
      sourceColor: 'bg-blue-100 text-blue-800',
      originalData: lead
    })),
    ...candidates.map(candidate => ({
      id: `candidate-${candidate.id}`,
      name: candidate.name,
      email: candidate.email,
      phone: candidate.phone,
      company: 'Candidate',
      position: candidate.role,
      type: 'candidate',
      location: candidate.location,
      notes: candidate.notes,
      linkedinUrl: candidate.linkedinUrl,
      source: 'recruitment',
      sourceLabel: 'Candidate',
      sourceIcon: UserPlus,
      sourceColor: 'bg-purple-100 text-purple-800',
      originalData: candidate
    }))
  ].filter(contact => contact.name && contact.name.trim() !== '');

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      type: 'general',
      location: '',
      notes: '',
      linkedinUrl: '',
      website: '',
      tags: '',
      lastContact: ''
    });
    setEditingContact(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingContact) {
      updateContact(editingContact.id, formData);
    } else {
      addContact(formData);
    }
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (contact) => {
    if (contact.source !== 'contacts') {
      // For non-contact sources, we can't edit here - would need to redirect
      alert('This contact is managed in the ' + contact.sourceLabel + ' section. Please edit it there.');
      return;
    }
    setFormData(contact);
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDelete = (contact) => {
    if (contact.source !== 'contacts') {
      alert('This contact is managed in the ' + contact.sourceLabel + ' section. Please delete it there.');
      return;
    }
    deleteContact(contact.id);
  };

  const filteredContacts = allContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.company && contact.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || 
                       (filterType === 'contacts_only' && contact.source === 'contacts') ||
                       contact.source === filterType ||
                       contact.type === filterType;
    
    const matchesViewMode = viewMode === 'all' || 
                           (viewMode === 'contacts_only' && contact.source === 'contacts');
    
    return matchesSearch && matchesType && matchesViewMode;
  });

  const getTypeInfo = (type) => contactTypes.find(t => t.value === type) || contactTypes[0];

  const stats = {
    total: allContacts.length,
    investors: allContacts.filter(c => c.source === 'investors').length,
    salesLeads: allContacts.filter(c => c.source === 'sales_leads').length,
    candidates: allContacts.filter(c => c.source === 'recruitment').length,
    general: allContacts.filter(c => c.source === 'contacts').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Contacts</h1>
          <p className="text-gray-600 mt-2">Unified view of all your business relationships</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Contact</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-600">Total Contacts</p>
          </div>
        </div>
        <div className="card">
          <div className="text-center">
            <p className="text-2xl font-semibold text-green-600">{stats.investors}</p>
            <p className="text-sm text-gray-600">Investors</p>
          </div>
        </div>
        <div className="card">
          <div className="text-center">
            <p className="text-2xl font-semibold text-blue-600">{stats.salesLeads}</p>
            <p className="text-sm text-gray-600">Sales Leads</p>
          </div>
        </div>
        <div className="card">
          <div className="text-center">
            <p className="text-2xl font-semibold text-purple-600">{stats.candidates}</p>
            <p className="text-sm text-gray-600">Candidates</p>
          </div>
        </div>
        <div className="card">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-600">{stats.general}</p>
            <p className="text-sm text-gray-600">General</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search contacts, companies, or emails..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="input-field"
          >
            <option value="all">All Sources</option>
            <option value="contacts_only">General Contacts Only</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-field"
          >
            <option value="all">All Types</option>
            <option value="investors">Investors</option>
            <option value="sales_leads">Sales Leads</option>
            <option value="recruitment">Candidates</option>
            <option value="contacts_only">General Contacts</option>
            {contactTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Contacts Grid */}
      {filteredContacts.length === 0 ? (
        <div className="card text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {allContacts.length === 0 ? "Your contact list is empty. Add some contacts to get started." : "Try adjusting your search or filters."}
          </p>
          <div className="mt-6">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredContacts.map((contact) => {
            const SourceIcon = contact.sourceIcon;
            const canEdit = contact.source === 'contacts';
            
            return (
              <div key={contact.id} className="card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{contact.name}</h3>
                    {contact.position && (
                      <p className="text-gray-600">{contact.position}</p>
                    )}
                    {contact.company && (
                      <div className="flex items-center mt-1">
                        <Building className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{contact.company}</span>
                      </div>
                    )}
                    {contact.location && (
                      <div className="flex items-center mt-1">
                        <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{contact.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {canEdit ? (
                      <>
                        <button
                          onClick={() => handleEdit(contact)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(contact)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Managed in {contact.sourceLabel}</span>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${contact.sourceColor}`}>
                      <SourceIcon className="inline mr-1 h-3 w-3" />
                      {contact.sourceLabel}
                    </span>
                  </div>

                  {contact.tags && (
                    <div>
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.split(',').slice(0, 3).map((tag, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {contact.lastContact && (
                    <div className="text-sm text-gray-500">
                      <Calendar className="inline mr-1 h-4 w-4" />
                      Last contact: {new Date(contact.lastContact).toLocaleDateString()}
                    </div>
                  )}

                  <div className="flex space-x-2 pt-3 border-t border-gray-100">
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title={contact.email}
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                        title={contact.phone}
                      >
                        <Phone size={16} />
                      </a>
                    )}
                    {contact.linkedinUrl && (
                      <a
                        href={contact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="LinkedIn Profile"
                      >
                        <Building size={16} />
                      </a>
                    )}
                    {contact.website && (
                      <a
                        href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                        title="Website"
                      >
                        <Building size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal - Only for general contacts */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-6">
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    className="input-field"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    placeholder="Phone"
                    className="input-field"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    className="input-field"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Position/Title"
                    className="input-field"
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                  />
                  <select
                    className="input-field"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                  >
                    {contactTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Location"
                    className="input-field"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                  <input
                    type="url"
                    placeholder="LinkedIn URL"
                    className="input-field"
                    value={formData.linkedinUrl}
                    onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})}
                  />
                </div>

                <input
                  type="text"
                  placeholder="Tags (comma separated)"
                  className="input-field"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                />

                <textarea
                  placeholder="Notes"
                  className="input-field h-24 resize-none"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingContact ? 'Update' : 'Add'} Contact
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;